import { Component, Input, Output, EventEmitter, inject, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Entity } from '../../../../core/data-models/Entity';
import { PlatformUser } from '../../../../core/data-models/platform-user';
import { LanguageService } from '../../../../core/services/language.service';
import { ModuleName } from '../../../../shared/enums/ModuleName';
import { ThemeModule } from '../../../../shared/Theme/theme.module';
import { ChildrenAnimation, inOutAnimation, slidesAniamtion } from '../../../../shared/animations/allAnimations';

// Re-export UserRoles enum - must match the one defined in settings.component.ts
export enum UserRoles {
    SuperAdmin = 'Super Admin',
    Admin = 'User',
    Owner = 'Owner'
}

export interface UsersTableOptions {
    data: PlatformUser[];
    columns: { field: string; title: string }[];
}

export interface UserAction {
    user: PlatformUser;
    index: number;
}

/**
 * Users Settings Component
 * 
 * Handles user management for Super Admin including:
 * - Display users in a table
 * - Add new user
 * - Edit existing user
 * - Change password
 * - Change email
 * - Reset password
 * - Activate/Deactivate user
 */
@Component({
    selector: 'users-settings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ThemeModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    templateUrl: './users-settings.component.html',
    styleUrls: ['./users-settings.component.scss'],
    animations: [inOutAnimation, slidesAniamtion, ChildrenAnimation]
})
export class UsersSettingsComponent {
    private languageService = inject(LanguageService);

    readonly ModuleNames = ModuleName;
    readonly UserRoles = UserRoles;

    @Input() users: PlatformUser[] = [];
    @Input() companies: Entity[] = [];
    @Input() currentUser!: PlatformUser;
    @Input() language: string = 'EN';
    @Input() usersOptions!: UsersTableOptions;

    @Output() addUser = new EventEmitter<void>();
    @Output() editUser = new EventEmitter<UserAction>();
    @Output() changePassword = new EventEmitter<PlatformUser>();
    @Output() changeEmail = new EventEmitter<PlatformUser>();
    @Output() resetPassword = new EventEmitter<string>(); // email
    @Output() deactivateUser = new EventEmitter<PlatformUser>();
    @Output() activateUser = new EventEmitter<PlatformUser>();

    /**
     * Extract name in current language from translation array
     */
    extractNameLanguage(name: any): string {
        return this.languageService.extractNameLanguage(name);
    }

    /**
     * Extract user's assigned companies as a comma-separated string
     */
    extractUserCompanies(u: PlatformUser): string {
        if (!this.companies) return '';

        let companyKeys: any[] = [];
        if (u.CompanyPermissions && u.CompanyPermissions.length > 0) {
            companyKeys = u.CompanyPermissions.filter(p => p.products && p.products.length > 0).map(p => p.key);
        } else if (u.EntityReferences && u.EntityReferences.length > 0) {
            companyKeys = u.EntityReferences;
        }

        return companyKeys
            .map(refKey => this.companies.find(c => c.key === refKey))
            .filter(company => !!company)
            .map(company => this.extractNameLanguage(company.name))
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(', ');
    }

    onAddUser(): void {
        this.addUser.emit();
    }

    onEditUser(index: number, user: PlatformUser): void {
        this.editUser.emit({ user, index });
    }

    onChangePassword(user: PlatformUser): void {
        this.changePassword.emit(user);
    }

    onChangeEmail(user: PlatformUser): void {
        this.changeEmail.emit(user);
    }

    onResetPassword(email: string): void {
        this.resetPassword.emit(email);
    }

    onDeactivateUser(user: PlatformUser): void {
        this.deactivateUser.emit(user);
    }

    onActivateUser(user: PlatformUser): void {
        this.activateUser.emit(user);
    }
}
